import { cargarDescartes } from "./alertasRecurrenciaDescarte";
import { normalizeMonthLocal } from "./cargarOportunidadesVentas";
import { agruparClientesPorEmpresa } from "./empresaContacto";
import { getDolarValue, loadExchangeRates } from "./exchangeRateService";
import { getClientes } from "./getClientes";
import { getContratos } from "./getContratos";
import { getOportunidades } from "./getOportunidades";
import { fetchMailSalienteResumen } from "./radarMailSaliente";

/**
 * Alertas de recurrencia comercial ("Radar Comercial") — docs/modulos/alertas-recurrencia-comercial.md.
 * Reglas vigentes: §15 de `docs/decisiones/2026-09-26-radar-mail-pagina-diseno.md` ("Reglas
 * definitivas del Radar" — reemplaza §2/§10/§14 de ese mismo doc). Principio rector: **el Radar no
 * desaparece solo nunca** — lo cierra el ejecutivo (tildando el check) o Postergar; ningún estado
 * por sí solo saca una fila del panel.
 *
 * Criterio de REFERENCIA (sin cambios desde el rework 2026-09-25): una empresa (agrupada por
 * 10.276, nunca por fila de `clientes`) que el AÑO PASADO, ese mismo mes calendario (`mes_cierre`),
 * o bien CERRÓ (100%, "compró") o bien tuvo una propuesta que quedó en 0% ("se propuso y no
 * cerró") — nunca las dos juntas, gana el cierre.
 *
 * Encendido (§15.1, Fase E Commit 2): por EVENTO, no por mes genérico — `DIAS_ANTICIPACION` (42)
 * días antes del ANIVERSARIO de la fecha exacta del evento (`fecha_cierre` 100% / `fecha_descarte`
 * 0%) si existe, o del día 1 del mes aniversario si solo hay mes; encendido hasta que termina el
 * mes del aniversario. Ver `ventanaEvento`.
 *
 * Visibilidad (§15, Fase E Commit 3): la función devuelve TODAS las candidatas cuya ventana está
 * encendida HOY y que no están postergadas/cerradas (`alertas_recurrencia_descartadas`,
 * `posponer_hasta >= hoy` — misma tabla para Postergar +7 días y para el check "fin del mes del
 * aniversario"). `cerroEsteAno`/`abiertaEsteAno` ya NO excluyen — son informativos, cambian la
 * PRESENTACIÓN vía `modo`, que decide la columna de acciones (sobres vs. check) Y la segunda parte
 * del `motivo` (ajuste visual 2026-09-26, ver `motivoAlerta` — el motivo YA NO se reemplaza entero
 * en ningún modo, siempre lleva la primera parte):
 *   - `"carrera"` (§15.4): hay una propuesta abierta este año y no cerró nada — sin sobres, check +
 *     motivo con segunda parte "Este año ya hay una propuesta en carrera".
 *   - `"contacto"` (§15.3): todos los lados con contacto con email (o el único que lo tiene) ya
 *     están escritos — sobres reemplazados por check + motivo con segunda parte "Cliente y/o
 *     agencia ya en contacto" (según `hayCliente`/`hayAgencia`).
 *   - `"sobres"` (§15.2/§15.7, el caso normal): cada lado independiente — `hayCliente`/
 *     `hayAgencia` (tiene contacto con email atribuible) × `escritoCliente`/`escritoAgencia` (mail
 *     saliente real después del encendido) deciden ámbar/verde/interrogación en el componente;
 *     `cerroEsteAno` agrega la segunda parte "Este año ya cerró otra venta" (§15.5).
 *
 * Cada alerta lleva además `clientId`/`salesId`/`esContrato` de la VENTA DE REFERENCIA (la más
 * reciente del grupo en ese mes del año pasado — 100% gana sobre 0% si hubiera ambas) y, de esa
 * misma venta, `campana`, `mesCierre`, `ano` (año de exhibición/consumo, 10.252 — NO el año en que
 * cerró), `edificiosTotales`, `edificiosParticipantes`. Sin `importe` (decisión de Ignacio, no va
 * al mail).
 *
 * Alcance: `sales` + `sales_contratos` (10.127); `sales_btl` FUERA. Uruguay fuera (10.181/10.267).
 * Conversión de moneda (10.185, TC del día 15 del mes de cierre) y agrupación por empresa (10.276)
 * sin cambios.
 */

export const MESES_ES = [
  "",
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

const ESTADOS_ABIERTOS = ["50%", "75%", "90%"];
const DIAS_ANTICIPACION = 42; // era 45 — §15.1

/**
 * Carga `sales` + `sales_contratos` + `clientes` de un país (oportunidades SIN filtrar por
 * estatus) y, con los datos ya en memoria, el mail saliente atribuible (`fetchMailSalienteResumen`,
 * consulta acotada nueva — ver ese archivo) y los descartes/postergaciones manuales. Ya NO trae la
 * actividad de CRM precalculada (`fetchResumenGrupos`): apagaba por CUALQUIER tipo de actividad
 * (MEET/CALL/MAIL, sin distinguir dirección) — retirado en la Fase A del rediseño (2026-09-26,
 * decisión de Ignacio: solo mail SALIENTE o cotización apagan). Loader LIVIANO a propósito (no
 * reusa el orquestador pesado `cargarOportunidadesVentas`).
 */
export const cargarDatosAlertasRecurrencia = async ({ pais }) => {
  const [oportunidadesData, contratosData, clientesData] = await Promise.all([
    getOportunidades({ pais }),
    getContratos({ pais }),
    getClientes({ pais }),
    loadExchangeRates(),
  ]);

  const oportunidades = (oportunidadesData || [])
    .filter((o) => !o.eliminada)
    .map((o) => ({
      id: o.id,
      esContrato: false,
      client_id: o.client_id,
      estatus: o.estatus || "0%",
      importe: o.importe,
      moneda_dolar: o.moneda_dolar,
      created_at: o.created_at,
      campana: o.campana,
      ano: o.ano,
      edificiosTotales: o.edificiostotales,
      edificiosParticipantes: o.edificiosparticipantes,
      mes_cierre: normalizeMonthLocal(o.mes_cierre, o.ano || o.ano_cierre, o.created_at, o.mes_cierre_old || o.mes_old),
      // Fecha exacta del evento (§15.1) — `fecha_descarte` todavía puede no existir en el schema
      // real (migración 20260926e sin aplicar): `o.fecha_descarte` simplemente viene `undefined`
      // hasta entonces (SELECT * de PostgREST, nunca un error — la columna no está en el SELECT
      // si no existe), y el cálculo de ventana cae sola al fallback de mes.
      fechaCierre: o.fecha_cierre ?? null,
      fechaDescarte: o.fecha_descarte ?? null,
    }));

  const contratos = (contratosData || [])
    .filter((c) => !c.eliminada)
    .map((c) => ({
      id: c.id,
      esContrato: true,
      client_id: c.client_id,
      estatus: c.estatus || "50%",
      importe: c.importe_total,
      moneda_dolar: c.moneda_dolar,
      created_at: c.created_at,
      campana: c.campana,
      ano: c.ano,
      edificiosTotales: c.edificios_referencia_totales,
      edificiosParticipantes: c.edificios_referencia,
      mes_cierre: normalizeMonthLocal(c.mes_cierre, c.ano, c.created_at, c.mes_inicio),
      fechaCierre: c.fecha_cierre ?? null,
      fechaDescarte: c.fecha_descarte ?? null,
    }));

  const clientes = clientesData || [];

  const [descartes, { saliente: mailSalienteResumen, contactoConEmailPorEmpresa }] = await Promise.all([
    cargarDescartes({ pais }),
    fetchMailSalienteResumen({ pais, clientes }),
  ]);

  return {
    oportunidades: [...oportunidades, ...contratos],
    clientes,
    descartes,
    mailSalienteResumen,
    contactoConEmailPorEmpresa,
  };
};

/**
 * Fase E, Commit 2 (§15.1 del diseño, 2026-09-26) — el encendido deja de ser por MES y pasa a ser
 * por EVENTO: cada venta (100%) o propuesta (0%) del año pasado enciende su propio aviso
 * `DIAS_ANTICIPACION` días antes de su ANIVERSARIO (mismo mes y día, un año después) si tiene
 * fecha exacta (`fecha_cierre` en el 100% — trigger `set_fecha_cierre_local`, aplicado desde
 * dic-2025 — o `fecha_descarte` en el 0%, misma función, migración `20260926e` ESCRITA SIN
 * APLICAR); si solo hay mes, se enciende `DIAS_ANTICIPACION` días antes del día 1 del mes
 * aniversario. En los dos casos queda encendido hasta que TERMINA el mes aniversario. `mesesActivos`
 * desaparece como concepto — ya no hay una lista chica de "meses candidatos" recorriendo TODAS las
 * empresas; se itera directo sobre los grupos (empresa, mes, año) que de verdad tienen un
 * cierre/propuesta el año pasado (`triplesConEvento`), y cada uno decide si HOY cae dentro de su
 * propia ventana. Cuando un grupo tiene VARIOS eventos (varias ventas/propuestas el mismo mes), el
 * grupo entero enciende con el PRIMERO de sus eventos (`encendido` mínimo).
 *
 * Ventana de encendido de UN evento (§15.1) — `encendido` (42 días antes del aniversario) y `fin`
 * (fin del mes aniversario, fin de día). `fechaExacta` es `fecha_cierre` (100%) o `fecha_descarte`
 * (0%) de la venta/propuesta de referencia; si es `null` (todavía no existe la fecha exacta —
 * hoy el caso normal, ver Commit 1), cae al fallback de "día 1 del mes aniversario".
 */
const ventanaEvento = ({ fechaExacta, mesObjetivo, anoObjetivo }) => {
  const fin = new Date(anoObjetivo, mesObjetivo, 0);
  fin.setHours(23, 59, 59, 999);

  const encendido = fechaExacta
    ? new Date(anoObjetivo, new Date(fechaExacta).getMonth(), new Date(fechaExacta).getDate())
    : new Date(anoObjetivo, mesObjetivo - 1, 1);
  encendido.setDate(encendido.getDate() - DIAS_ANTICIPACION);
  encendido.setHours(0, 0, 0, 0);

  return { encendido, fin };
};

// Exportado — el tooltip del sobre verde ("Le escribiste el d/m", §15.2) lo usa
// `AlertasRecurrenciaBanner` sobre `alerta.fechaEscritoCliente`/`fechaEscritoAgencia`.
export const formatDiaMes = (fecha) => {
  const d = new Date(fecha);
  return `${d.getDate()}/${d.getMonth() + 1}`;
};

/**
 * Calcula TODAS las alertas de recurrencia VISIBLES hoy (ventana de encendido abierta, no
 * postergadas/cerradas) — §15, Fase E. Función pura, testeable.
 *
 * @returns {Array<{tipo, empresaNorm, empresaDisplay, mesObjetivo, anoObjetivo, anoPasado, monto,
 *   veces, motivo, cerroEsteAno, abiertaEsteAno, modo: 'carrera'|'contacto'|'sobres', hayCliente,
 *   hayAgencia, escritoCliente, escritoAgencia, fechaEscritoCliente, fechaEscritoAgencia, clientId,
 *   salesId, esContrato, campana, mesCierre, ano, edificiosTotales, edificiosParticipantes}>}
 *   ordenado por `monto` descendente.
 */
export const calcularAlertasRecurrencia = ({
  oportunidades,
  clientes,
  descartes = null,
  mailSalienteResumen = [],
  contactoConEmailPorEmpresa = [],
  getDolarValueFn = getDolarValue,
  hoy = new Date(),
  pais,
}) => {
  const gruposEmpresa = agruparClientesPorEmpresa(clientes);
  const empresaPorClienteId = new Map();
  for (const [, grupo] of gruposEmpresa) {
    for (const clienteId of grupo.clienteIds) empresaPorClienteId.set(String(clienteId), grupo.empresaNorm);
  }

  // Por lado (§15.2/§15.3/§15.7): fecha del último mail SALIENTE atribuible por (empresa, lado) y
  // qué lados tienen contacto con email atribuible — las dos vienen de `radarMailSaliente.js`
  // (mismo pool no-ambiguo, sin query extra).
  const ultimaSalientePorClave = new Map(
    mailSalienteResumen.map((m) => [`${m.empresa_norm}|${m.lado}`, m.ultima_fecha])
  );
  const ladosConContactoPorEmpresa = new Map(contactoConEmailPorEmpresa.map((c) => [c.empresa_norm, new Set(c.lados)]));

  // Postergada (§15.6, +7 días) O cerrada con el check (§15.3/§15.4/§15.9, fin del mes del
  // aniversario) — MISMA tabla, dos valores de `posponer_hasta` distintos escritos por dos
  // funciones (`postergarAlerta`/`cerrarAlertaHastaElAnoQueViene`); acá da igual cuál fue: si
  // sigue vigente, la fila simplemente no aparece. Comparación por fecha local (no UTC).
  const hoyLocalISO = (() => {
    const d = new Date(hoy);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const posponerHastaPorClave = new Map(
    (descartes || [])
      .filter((d) => d.posponer_hasta && d.posponer_hasta >= hoyLocalISO)
      .map((d) => [`${d.empresa_norm}|${d.mes}|${d.ano}`, d.posponer_hasta])
  );

  const sumas = new Map(); // `${empresaNorm}|${mes}|${ano}|${'100'|'0'}` -> { suma, veces, refVenta, encendido, fin }
  const abiertasPorGrupo = new Set(); // `${empresaNorm}|${mes}|${ano}` con 50/75/90 apuntando ahí
  // Candidatas a alerta (empresa/mes/año con un 100% o un 0%). Guarda los 3 valores YA
  // DESCOMPUESTOS (nunca reconstruye `empresaNorm` volviendo a partir la clave compuesta con
  // `.split("|")`): `empresa.pais='Perú'` incluye un nombre real con un `|` LITERAL
  // ("CREDICORP | BCP", ver "Hallazgo de datos, no bug" en la ficha del módulo) que rompería el
  // separador si se intentara reconstruir la empresa desde el string.
  const triplesConEvento = new Map(); // clave string (dedup) -> { empresaNorm, mes, ano }

  for (const o of oportunidades) {
    const empresaNorm = empresaPorClienteId.get(String(o.client_id));
    if (!empresaNorm || !o.mes_cierre) continue;
    const [mesStr, anoStr] = o.mes_cierre.split("-");
    const mes = parseInt(mesStr, 10);
    const ano = parseInt(anoStr, 10);
    if (!mes || !ano) continue;

    if (o.estatus === "100%" || o.estatus === "0%") {
      const esDolar = o.moneda_dolar === true;
      const importe = Number(o.importe) || 0;
      const rate = esDolar ? getDolarValueFn(`${anoStr}-${mesStr}-15`, pais) || 1 : 1;
      const montoLocal = esDolar ? importe * rate : importe;
      const key = `${empresaNorm}|${mes}|${ano}|${o.estatus === "100%" ? "100" : "0"}`;
      const fechaExacta = o.estatus === "100%" ? o.fechaCierre : o.fechaDescarte;
      const anoObjetivoEvento = ano + 1; // el aniversario de ESTE evento cae el año siguiente al suyo
      const { encendido, fin } = ventanaEvento({ fechaExacta, mesObjetivo: mes, anoObjetivo: anoObjetivoEvento });

      const acc = sumas.get(key) || { suma: 0, veces: 0, refVenta: null, encendido, fin };
      acc.suma += montoLocal;
      acc.veces += 1;
      if (encendido < acc.encendido) acc.encendido = encendido; // el grupo enciende con el primero de sus eventos
      if (!acc.refVenta || o.created_at > acc.refVenta.created_at) acc.refVenta = o;
      sumas.set(key, acc);
      triplesConEvento.set(`${empresaNorm}|${mes}|${ano}`, { empresaNorm, mes, ano });
    } else if (ESTADOS_ABIERTOS.includes(o.estatus)) {
      abiertasPorGrupo.add(`${empresaNorm}|${mes}|${ano}`);
    }
  }

  const alertas = [];
  for (const { empresaNorm, mes, ano: anoPasado } of triplesConEvento.values()) {
    const anoObjetivo = anoPasado + 1;
    const grupo = gruposEmpresa.get(empresaNorm);
    if (!grupo) continue;

    const sum100 = sumas.get(`${empresaNorm}|${mes}|${anoPasado}|100`);
    const sum0 = sumas.get(`${empresaNorm}|${mes}|${anoPasado}|0`);
    let tipo;
    let base;
    if (sum100) {
      tipo = "compro";
      base = sum100;
    } else if (sum0) {
      tipo = "propuso";
      base = sum0;
    } else {
      continue;
    }

    // ¿Está encendido HOY? (§15.1)
    if (hoy < base.encendido || hoy > base.fin) continue;

    const keyEsteAno = `${empresaNorm}|${mes}|${anoObjetivo}`;

    // Postergada o cerrada con el check — NINGÚN otro estado saca una fila del panel por sí solo
    // (principio de §15: "el Radar no desaparece solo nunca"). Único gate de visibilidad además
    // de la ventana de encendido de arriba.
    if (posponerHastaPorClave.has(keyEsteAno)) continue;

    const cerroEsteAno = sumas.has(`${keyEsteAno}|100`); // §15.5 — informativo, ya NO excluye
    const abiertaEsteAno = abiertasPorGrupo.has(keyEsteAno); // §15.4 — informativo, ya NO excluye

    const ladosConContacto = ladosConContactoPorEmpresa.get(empresaNorm) || new Set();
    const hayCliente = ladosConContacto.has("cliente");
    const hayAgencia = ladosConContacto.has("agencia");
    const fechaEscritoCliente = ultimaSalientePorClave.get(`${empresaNorm}|cliente`);
    const fechaEscritoAgencia = ultimaSalientePorClave.get(`${empresaNorm}|agencia`);
    const escritoCliente = !!fechaEscritoCliente && new Date(fechaEscritoCliente) >= base.encendido;
    const escritoAgencia = !!fechaEscritoAgencia && new Date(fechaEscritoAgencia) >= base.encendido;

    // Modo de la columna de acciones — UNA sola fuente para el panel Y el contador del header
    // (10.229): ninguno de los dos recalcula esto por su cuenta.
    //   - "carrera" (§15.4): hay una propuesta abierta este año Y NO cerró nada — sin sobres.
    //   - "contacto" (§15.3): todos los lados con contacto con email ya están escritos (o el
    //     único lado que tiene contacto ya está escrito) — sobres reemplazados por texto+check.
    //   - "sobres" (§15.2/§15.7): el caso normal — cada lado ámbar/verde/interrogación por su cuenta.
    let modo;
    if (abiertaEsteAno && !cerroEsteAno) {
      modo = "carrera";
    } else {
      const algunLadoConContacto = hayCliente || hayAgencia;
      const todosLosLadosEscritos =
        algunLadoConContacto && (!hayCliente || escritoCliente) && (!hayAgencia || escritoAgencia);
      modo = todosLosLadosEscritos ? "contacto" : "sobres";
    }

    const ref = base.refVenta;
    const motivo = motivoAlerta({
      tipo,
      mesObjetivo: mes,
      veces: base.veces,
      campana: ref?.campana ?? null,
      modo,
      cerroEsteAno,
      hayCliente,
      hayAgencia,
    });

    alertas.push({
      tipo,
      empresaNorm,
      empresaDisplay: grupo.empresaDisplay,
      mesObjetivo: mes,
      anoObjetivo,
      anoPasado,
      monto: Math.round(base.suma),
      veces: base.veces,
      motivo,
      cerroEsteAno,
      abiertaEsteAno,
      modo,
      hayCliente,
      hayAgencia,
      escritoCliente,
      escritoAgencia,
      fechaEscritoCliente: escritoCliente ? fechaEscritoCliente : null,
      fechaEscritoAgencia: escritoAgencia ? fechaEscritoAgencia : null,
      clientId: ref?.client_id ?? null,
      salesId: ref?.id ?? null,
      esContrato: ref?.esContrato ?? false,
      campana: ref?.campana ?? null,
      mesCierre: ref?.mes_cierre ?? null,
      ano: ref?.ano ?? null,
      edificiosTotales: ref?.edificiosTotales ?? null,
      edificiosParticipantes: ref?.edificiosParticipantes ?? null,
    });
  }

  return alertas.sort((a, b) => b.monto - a.monto);
};

/**
 * ÚNICA fuente del texto de cada fila (10.229) — arma `alerta.motivo` completo, en dos partes
 * (ajuste visual 2026-09-26; `calcularAlertasRecurrencia` ya no compone ni reemplaza nada por su
 * cuenta). Primera parte, SIEMPRE, según `tipo`, con la campaña de la venta de referencia tal cual
 * está guardada en la DB (sin transformar) cuando `veces===1`, "`N` campañas" si hubo más de una
 * el mismo mes/año, o "una campaña" si `campana` viene null (nunca se deja el hueco vacío):
 *   - "compro": "En {mes} del año pasado compró {campaña}."
 *   - "propuso": "En {mes} del año pasado propusimos {campaña} y no cerró." (plural "propusimos" a
 *     propósito — la propuesta fue del equipo, no del cliente; texto neutro, el aviso lo ve todo
 *     el equipo).
 * Segunda parte, condicional según `modo`/`cerroEsteAno`, se agrega con un espacio (NUNCA
 * reemplaza la primera — el modo "carrera" antes de este ajuste hacía justamente eso):
 *   - "carrera" (§15.4): "Este año ya hay una propuesta en carrera."
 *   - "contacto" (§15.3): "Cliente y agencia ya en contacto." / "Cliente ya en contacto." /
 *     "Agencia ya en contacto." — mismo criterio `hayCliente`/`hayAgencia` que decide los sobres:
 *     si un lado no tiene contacto, el texto no lo nombra.
 *   - "sobres" con `cerroEsteAno` (§15.5): "Este año ya cerró otra venta."
 *   - "sobres" sin `cerroEsteAno`: sin segunda parte.
 */
export const motivoAlerta = (alerta) => {
  const mes = MESES_ES[alerta.mesObjetivo];
  const campanaTxt = alerta.veces > 1 ? `${alerta.veces} campañas` : alerta.campana || "una campaña";
  const parte1 =
    alerta.tipo === "compro"
      ? `En ${mes} del año pasado compró ${campanaTxt}.`
      : `En ${mes} del año pasado propusimos ${campanaTxt} y no cerró.`;

  let parte2 = "";
  if (alerta.modo === "carrera") {
    parte2 = "Este año ya hay una propuesta en carrera.";
  } else if (alerta.modo === "contacto") {
    parte2 =
      alerta.hayCliente && alerta.hayAgencia
        ? "Cliente y agencia ya en contacto."
        : alerta.hayCliente
          ? "Cliente ya en contacto."
          : "Agencia ya en contacto.";
  } else if (alerta.cerroEsteAno) {
    parte2 = "Este año ya cerró otra venta.";
  }

  return parte2 ? `${parte1} ${parte2}` : parte1;
};

/**
 * Texto de estado para la columna de acciones de quien SOLO VE el Radar (`puedeActuarRadar` en
 * falso, sin sobres ni botón check) — resume el mismo `modo` que ve quien actúa (10.229: mismo
 * predicado, nunca un texto genérico aparte). "sobres" se abre en las 4 combinaciones posibles de
 * `hayCliente`/`hayAgencia`/`escritoCliente`/`escritoAgencia`.
 */
export const textoEstadoRadar = (alerta) => {
  if (alerta.modo === "carrera") return "Propuesta en carrera";
  if (alerta.modo === "contacto") return "Ya en contacto";
  const faltaCliente = alerta.hayCliente && !alerta.escritoCliente;
  const faltaAgencia = alerta.hayAgencia && !alerta.escritoAgencia;
  if (faltaCliente && faltaAgencia) return "Falta escribir";
  if (faltaCliente) return "Falta escribir al cliente";
  if (faltaAgencia) return "Falta escribir a la agencia";
  return "Sin contactos con email";
};

/**
 * Contador del header (§15.8) — filas con al menos un sobre ÁMBAR más filas con check SIN
 * TILDAR (`modo` "contacto"/"carrera" — si ya se tildó, la fila ni siquiera llega hasta acá,
 * `calcularAlertasRecurrencia` la excluye por `posponer_hasta`). Postergadas no cuentan (mismo
 * motivo: ya fueron excluidas antes). Fuente ÚNICA (10.229) — la usan el badge del botón Y
 * cualquier otra superficie que necesite "cuántas necesitan acción"; el panel en sí sigue
 * mostrando TODAS las filas de `alertas` (puede haber más filas visibles que el número del badge —
 * caso borde: una empresa sin NINGÚN contacto en ningún lado, sobres "interrogación" los dos, no
 * tiene ni un sobre ámbar ni un check para tildar).
 */
export const cuentaEnContadorHeader = (alerta) => {
  if (alerta.modo === "contacto" || alerta.modo === "carrera") return true;
  return (alerta.hayCliente && !alerta.escritoCliente) || (alerta.hayAgencia && !alerta.escritoAgencia);
};

/**
 * Formato compacto ($180M / $45K, o S/ 458K en Perú) para el total del encabezado del panel — la
 * suma de los montos YA convertidos (10.185), sin volver a tocar la conversión. `simbolo` lo decide
 * el caller según la moneda LOCAL del país (`monedaLocalDePais`, `src/utils/brutoCapex.js` — 10.1);
 * default "$" mantiene el comportamiento de Chile sin tocar al resto de los callers. No hay un
 * formateador compacto centralizado en el proyecto (10.27); acotado a esta única línea, las filas
 * siguen con `formatAmount`/`useCurrency` completo (10.1).
 */
export const formatMontoCompacto = (monto, simbolo = "$") => {
  const abs = Math.abs(monto);
  if (abs >= 1_000_000) return `${simbolo} ${Math.round(monto / 1_000_000)}M`;
  if (abs >= 1_000) return `${simbolo} ${Math.round(monto / 1_000)}K`;
  return `${simbolo} ${Math.round(monto)}`;
};
